import React from 'react';
import { ArticleRichText } from '../components/ArticleRichText/ArticleRichText';
import { CompetitionProps } from '../views/Competition/Competition';
import { articleToBreadcrumbs } from './contentfulApiArticleToArticleProps';
import { format, isWithinInterval, parseISO, isAfter } from 'date-fns';
import { ContentfulCompetition, EntryType } from '@jbhi-fi/apis/src/contentful/types';
import { AssetTransformer } from '@jbhi-fi/components/src/Contentful/transformers/AssetTransformer';
import { CardTransformer } from '@jbhi-fi/components/src/Contentful/transformers/CardTransformer';
import { getLocalTimeWithoutTimezone } from '@jbhi-fi/utils/src/date';
import { palette } from '@jbhi-fi/lanyard-ui/themes';

export const contentfulApiCompetitionToCompetitionProps = (
  competition: ContentfulCompetition,
  {
    customerFirstName,
    alreadyEnteredCompetitionText,
  }: { customerFirstName?: string; alreadyEnteredCompetitionText?: string } = {}
): CompetitionProps => {
  const { fields } = competition;
  const coverImage = fields?.heroImage?.fields ? AssetTransformer.toImageProps(fields.heroImage) : null;

  return {
    competitionId: competition.sys.id,
    header: {
      coverImgSrc: coverImage?.src ?? '',
      coverImgAlt: coverImage?.alt ?? '',
    },
    competitionBlock: {
      heading: fields?.heading ?? '',
      intro: fields?.intro ?? '',
      content: fields?.mainBody ? <ArticleRichText document={fields.mainBody} /> : null,
      tag: CardTransformer.toTagProps(competition, { includeSubCategoryHref: true }),
      winnerAnnouncementDate: fields?.winnerAnnouncementDate
        ? format(parseISO(fields.winnerAnnouncementDate), 'd MMM yyyy')
        : '',
      endDate: fields?.endDate ? format(parseISO(fields.endDate), 'd MMM yyyy') : '',
      openingDate: getOpeningDate(competition),
      isOpen: getIsOpen(competition),
      alreadyEnteredText: alreadyEnteredCompetitionText?.replace('{{firstName}}', customerFirstName ?? '') ?? '',
    },
    breadcrumbs: {
      colorHex: palette.primary.neutral,
      items: articleToBreadcrumbs(competition),
    },
    relatedEntries: {
      heading: fields?.parentPage?.fields
        ? `More from ${fields?.parentPage?.fields?.categoryPage?.fields?.heading}`
        : '',
      contentType: EntryType.competition,
      filter: fields?.parentPage?.fields?.categoryPage?.sys
        ? {
            'fields.parentPage.sys.contentType.sys.id': EntryType.subCategory,
            'fields.parentPage.fields.categoryPage.sys.id': fields.parentPage.fields.categoryPage.sys.id,
            'fields.endDate[gte]': getLocalTimeWithoutTimezone(new Date()),
            'sys.id[nin]': competition.sys.id,
          }
        : {
            'fields.endDate[gte]': getLocalTimeWithoutTimezone(new Date()),
          },
    },
  };
};

const getIsOpen = (competition: ContentfulCompetition): boolean => {
  const { fields } = competition;
  if (!fields) return true;

  return isWithinInterval(new Date(), { start: parseISO(fields.startDate), end: parseISO(fields.endDate) });
};

const getOpeningDate = (competition: ContentfulCompetition): string | undefined => {
  const { fields } = competition;
  if (!fields) return undefined;

  return isAfter(new Date(), parseISO(fields.endDate)) ? undefined : format(parseISO(fields.startDate), 'd MMM yyyy');
};
